import {
  handleResp,
  getURLWithQueryParams,
  getDefaultHeaders,
  extendFetchOptions,
} from "./utils";
import type {
  WrappedCashifyFunction,
  GenericResponse,
  CommonResponse,
  WrappedCashifyPageableQuery,
} from "./utils";

import type {
  ApplicationFormDTO,
  ApplicationFormSearchCriteria,
  ApplicationFormDetail,
  ApplicationFormCreateRequest,
  ApplicationFormSchema,
  ApplicationFormExampleDTO,
  ApplicationFormExampleCreateRequest,
} from "./application-types";
import { PortalDisplaySchema } from "utils/application-schema-state";
import { StandardObject } from "utils";

export interface ApplicationFormService {
  listApplicationForm: ReturnType<typeof listApplicationForm>;
  createApplicationForm: ReturnType<typeof createApplicationForm>;
  deleteApplicationForm: ReturnType<typeof deleteApplicationForm>;
  putApplicationFormMetadata: ReturnType<typeof putApplicationFormMetadata>;
  publishApplicationForm: ReturnType<typeof publishApplicationForm>;
  deprecateApplicationForm: ReturnType<typeof deprecateApplicationForm>;
  getApplicationFormDetail: ReturnType<typeof getApplicationFormDetail>;
  putApplicationFormFieldSchema: ReturnType<
    typeof putApplicationFormFieldSchema
  >;
  patchApplicationFormFieldSchema: ReturnType<
    typeof patchApplicationFormFieldSchema
  >;
  putApplicationFormPortalDisplaySchema: ReturnType<
    typeof putApplicationFormPortalDisplaySchema
  >;
  putApplicationFormGroup: ReturnType<typeof putApplicationFormGroup>;
  listApplicationFormExample: ReturnType<typeof listApplicationFormExample>;
  deleteApplicationFormExample: ReturnType<typeof deleteApplicationFormExample>;
  putApplicationFormExample: ReturnType<typeof putApplicationFormExample>;
  createApplicationFormExample: ReturnType<typeof createApplicationFormExample>;
}

export const listApplicationForm: WrappedCashifyPageableQuery<
  ApplicationFormSearchCriteria,
  ApplicationFormDTO
> = ({ urlPrefix, options }) => {
  return async (queryParams) => {
    let url = getURLWithQueryParams(
      `${urlPrefix}/api/v1/application-form`,
      queryParams
    );
    let opts = extendFetchOptions(
      {
        method: "GET",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const getApplicationFormDetail: WrappedCashifyFunction<
  { formId: string },
  GenericResponse<ApplicationFormDetail>
> = ({ urlPrefix, options }) => {
  return async ({ formId }) => {
    let url = `${urlPrefix}/api/v1/application-form/${formId}`;
    let opts = extendFetchOptions(
      {
        method: "GET",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const deleteApplicationForm: WrappedCashifyFunction<
  { formId: string },
  CommonResponse
> = ({ urlPrefix, options }) => {
  return async ({ formId }) => {
    let url = `${urlPrefix}/api/v1/application-form/${formId}`;
    let opts = extendFetchOptions(
      {
        method: "DELETE",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const createApplicationForm: WrappedCashifyFunction<
  ApplicationFormCreateRequest,
  CommonResponse
> = ({ urlPrefix, options }) => {
  return async ({ formName, duplicateFromFormId }) => {
    const fields: ApplicationFormCreateRequest = {
      formName,
    };
    if (!!duplicateFromFormId) {
      fields["duplicateFromFormId"] = duplicateFromFormId;
    }
    let url = `${urlPrefix}/api/v1/application-form`;
    let opts = extendFetchOptions(
      {
        method: "POST",
        body: JSON.stringify(fields),
        headers: {
          ...getDefaultHeaders(),
          "Content-Type": "application/json",
        },
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const publishApplicationForm: WrappedCashifyFunction<
  { formId: string },
  CommonResponse
> = ({ urlPrefix, options }) => {
  return async ({ formId }) => {
    let url = `${urlPrefix}/api/v1/application-form/${formId}/publish`;
    let opts = extendFetchOptions(
      {
        method: "POST",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const deprecateApplicationForm: WrappedCashifyFunction<
  { formId: string },
  CommonResponse
> = ({ urlPrefix, options }) => {
  return async ({ formId }) => {
    let url = `${urlPrefix}/api/v1/application-form/${formId}/deprecate`;
    let opts = extendFetchOptions(
      {
        method: "POST",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

const updateApplicationFormFieldSchema: (
  method: "PUT" | "PATCH"
) => WrappedCashifyFunction<
  {
    formId: string;
    itemSchemas: ApplicationFormSchema[];
    portalCustomizations?: { itemSchemaOrder: string[] };
  },
  CommonResponse
> =
  (method: "PUT" | "PATCH") =>
  ({ urlPrefix, options }) => {
    return async ({ formId, itemSchemas, portalCustomizations }) => {
      const body: {
        itemSchemas: ApplicationFormSchema[];
        portalCustomizations?: { itemSchemaOrder: string[] };
      } = {
        itemSchemas,
      };
      if (!!portalCustomizations) {
        body["portalCustomizations"] = portalCustomizations;
      }
      let url = `${urlPrefix}/api/v1/application-form/${formId}/field-schema`;
      let opts = extendFetchOptions(
        {
          method,
          headers: {
            ...getDefaultHeaders(),
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        },
        options
      );
      return fetch(url, opts).then((resp) => handleResp(resp));
    };
  };

export const putApplicationFormFieldSchema =
  updateApplicationFormFieldSchema("PUT");

export const patchApplicationFormFieldSchema =
  updateApplicationFormFieldSchema("PATCH");

export const putApplicationFormPortalDisplaySchema: WrappedCashifyFunction<
  { formId: string; portalDisplaySchema: PortalDisplaySchema },
  CommonResponse
> = ({ urlPrefix, options }) => {
  return async ({ formId, portalDisplaySchema }) => {
    let url = `${urlPrefix}/api/v1/application-form/${formId}/portal-display-schema`;
    let opts = extendFetchOptions(
      {
        method: "PUT",
        headers: {
          ...getDefaultHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(portalDisplaySchema),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const putApplicationFormGroup: WrappedCashifyFunction<
  { formId: string; groupId: string },
  CommonResponse
> = ({ urlPrefix, options }) => {
  return async ({ formId, groupId }) => {
    let url = getURLWithQueryParams(
      `${urlPrefix}/api/v1/application-form/${formId}/group`,
      {
        groupId,
      },
      true
    );
    let opts = extendFetchOptions(
      {
        method: "PUT",
        headers: {
          ...getDefaultHeaders(),
          "Content-Type": "application/json",
        },
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const listApplicationFormExample: WrappedCashifyPageableQuery<
  { formId: string },
  ApplicationFormExampleDTO
> = ({ urlPrefix, options }) => {
  return async (params) => {
    const { formId, pageNumber, pageSize } = params;
    let url = getURLWithQueryParams(
      `${urlPrefix}/api/v1/application-form/${formId}/example`,
      {
        pageNumber,
        pageSize,
      }
    );
    let opts = extendFetchOptions(
      {
        method: "GET",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const deleteApplicationFormExample: WrappedCashifyFunction<
  { formId: string; exampleId: number },
  CommonResponse
> = ({ urlPrefix, options }) => {
  return async ({ formId, exampleId }) => {
    let url = `${urlPrefix}/api/v1/application-form/${formId}/example/${exampleId}`;
    let opts = extendFetchOptions(
      {
        method: "DELETE",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const putApplicationFormExample: WrappedCashifyFunction<
  { formId: string; exampleId: string; exampleData: StandardObject },
  CommonResponse
> = ({ urlPrefix, options }) => {
  return async ({ formId, exampleId, exampleData }) => {
    let url = `${urlPrefix}/api/v1/application-form/${formId}/example/${exampleId}`;
    let opts = extendFetchOptions(
      {
        method: "PUT",
        headers: {
          ...getDefaultHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(exampleData),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const createApplicationFormExample: WrappedCashifyFunction<
  ApplicationFormExampleCreateRequest,
  CommonResponse
> = ({ urlPrefix, options }) => {
  return async ({ formId, exampleName, duplicateFromExampleId }) => {
    const fields: Omit<ApplicationFormExampleCreateRequest, "formId"> = {
      exampleName,
    };
    if (!!duplicateFromExampleId) {
      fields["duplicateFromExampleId"] = duplicateFromExampleId;
    }
    let url = `${urlPrefix}/api/v1/application-form/${formId}/example`;
    let opts = extendFetchOptions(
      {
        method: "POST",
        body: JSON.stringify(fields),
        headers: {
          ...getDefaultHeaders(),
          "Content-Type": "application/json",
        },
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const putApplicationFormMetadata: WrappedCashifyFunction<
  { formId: string; formName: string },
  CommonResponse
> = ({ urlPrefix, options }) => {
  return async ({ formId, formName }) => {
    let url = `${urlPrefix}/api/v1/application-form/${formId}`;
    let opts = extendFetchOptions(
      {
        method: "PUT",
        headers: {
          ...getDefaultHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          formName,
        }),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};
